import { useId, type ReactNode } from 'react';
import { Minus, Plus } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

export interface NumberStepperProps {
  label?: string;
  helperText?: ReactNode;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** The 42px white field of the staff dialogs (Figma A04.1 "Add staff"). */
  compact?: boolean;
  className?: string;
}

/** Whole-number field with − / + buttons (Figma "Max active tasks"). Typing is allowed; the value is clamped. */
export function NumberStepper({
  label,
  helperText,
  value,
  onChange,
  min = 1,
  max = 99,
  compact = false,
  className,
}: NumberStepperProps) {
  const id = useId();
  const clamp = (n: number) => Math.min(max, Math.max(min, Math.round(n)));
  const step = (delta: number) => onChange(clamp(value + delta));
  const button =
    'flex size-(--mbm-space-7) items-center justify-center rounded-full text-text-secondary hover:text-text-primary disabled:cursor-not-allowed disabled:text-text-disabled focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)';

  return (
    <div className={cx('flex flex-col', compact ? 'gap-(--mbm-space-1-5)' : 'gap-(--mbm-space-2)', className)}>
      {label ? (
        <label htmlFor={id} className="text-label-medium text-text-secondary">
          {label}
        </label>
      ) : null}
      <div
        className={cx(
          'flex items-center border border-border-default bg-surface-card focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-focus-ring',
          compact
            ? 'h-(--mbm-size-input-compact) rounded-(--mbm-radius-sm) pl-(--mbm-space-3) pr-(--mbm-space-2)'
            : 'h-(--mbm-size-input) rounded-(--mbm-component-radius-input) pl-(--mbm-space-4) pr-(--mbm-space-2)',
        )}
      >
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(clamp(Number(e.target.value) || min))}
          className={cx(
            'h-full min-w-0 flex-1 bg-transparent text-text-primary outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none',
            compact ? 'text-body-medium' : 'text-body-large',
          )}
        />
        <button type="button" aria-label="Decrease" disabled={value <= min} onClick={() => step(-1)} className={button}>
          <Icon icon={Minus} size="sm" />
        </button>
        <button type="button" aria-label="Increase" disabled={value >= max} onClick={() => step(1)} className={button}>
          <Icon icon={Plus} size="sm" />
        </button>
      </div>
      {helperText ? (
        <p className={compact ? 'text-caption text-text-tertiary' : 'text-body-small text-text-tertiary'}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
